import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  ModelConfigService,
  ModelProfileSummary,
  UseCaseDefault,
  ProjectModelConfig,
  UseCaseModelAssignment,
} from '../../../../shared/model-config.service';
import { ProjectMetadataService } from '../../../../shared/project-metadata.service';
import { ClientLoggingService } from '../../../../shared/client-logging.service';

interface UseCaseRow {
  useCase: string;
  displayName: string;
  defaultProfileId: string;
  selectedProfileId: string;
  isOverridden: boolean;
  roleProfiles: Record<string, string>;
}

/** Card for selecting which model profile is used per LLM use case at the project level (Issue #1020). */
@Component({
  selector: 'app-model-config-card',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
  ],
  templateUrl: './model-config-card.component.html',
  styleUrl: './model-config-card.component.scss',
})
export class ModelConfigCardComponent implements OnInit {
  @Input() projectId = '';
  @Input() projectModelConfig: ProjectModelConfig | null = null;

  profiles: ModelProfileSummary[] = [];
  useCaseRows: UseCaseRow[] = [];
  loading = true;
  saving = false;
  hasUnsavedChanges = false;
  error = '';

  private modelConfigService = inject(ModelConfigService);
  private projectMetadataService = inject(ProjectMetadataService);
  private originalOverrides: Map<string, string> = new Map();

  ngOnInit(): void {
    this.loadDefaults();
  }

  private loadDefaults(): void {
    this.loading = true;
    this.modelConfigService.getModelConfigDefaults().subscribe({
      next: defaults => {
        this.profiles = defaults.profiles;
        this.buildRows(defaults.useCaseDefaults);
        this.loading = false;
      },
      error: err => {
        ClientLoggingService.log('ERROR', '[ModelConfigCard] Failed to load defaults:', err);
        this.error = 'Failed to load model configuration.';
        this.loading = false;
      },
    });
  }

  private buildRows(useCaseDefaults: UseCaseDefault[]): void {
    const overrideMap = new Map<string, string>();
    if (this.projectModelConfig?.useCaseOverrides) {
      for (const override of this.projectModelConfig.useCaseOverrides) {
        if (override.profileId) {
          overrideMap.set(override.useCase, override.profileId);
        }
      }
    }

    this.useCaseRows = useCaseDefaults.map(uc => {
      const projectOverride = this.projectModelConfig?.useCaseOverrides.find(
        override => override.useCase === uc.useCase
      );
      const overrideProfileId = projectOverride?.profileId || overrideMap.get(uc.useCase);
      const selectedProfileId = overrideProfileId || uc.defaultProfileId;
      return {
        useCase: uc.useCase,
        displayName: uc.displayName,
        defaultProfileId: uc.defaultProfileId,
        selectedProfileId,
        isOverridden: !!overrideProfileId,
        roleProfiles: { ...(projectOverride?.roleProfiles || {}) },
      };
    });

    this.originalOverrides = new Map(overrideMap);
    this.hasUnsavedChanges = false;
  }

  onProfileChange(row: UseCaseRow, profileId: string): void {
    row.selectedProfileId = profileId;
    row.isOverridden = profileId !== row.defaultProfileId;
    this.checkForChanges();
  }

  resetToDefault(row: UseCaseRow): void {
    row.selectedProfileId = row.defaultProfileId;
    row.isOverridden = false;
    this.checkForChanges();
  }

  private checkForChanges(): void {
    const currentOverrides = new Map<string, string>();
    for (const row of this.useCaseRows) {
      if (row.isOverridden) {
        currentOverrides.set(row.useCase, row.selectedProfileId);
      }
    }

    if (currentOverrides.size !== this.originalOverrides.size) {
      this.hasUnsavedChanges = true;
      return;
    }
    for (const [key, value] of currentOverrides) {
      if (this.originalOverrides.get(key) !== value) {
        this.hasUnsavedChanges = true;
        return;
      }
    }
    this.hasUnsavedChanges = false;
  }

  async saveChanges(): Promise<void> {
    if (!this.projectId || this.saving) return;

    this.saving = true;
    try {
      const overrides: UseCaseModelAssignment[] = this.useCaseRows
        .filter(row => row.isOverridden)
        .map(row => ({
          useCase: row.useCase,
          profileId: row.selectedProfileId,
          roleProfiles: row.roleProfiles,
        }));

      const modelConfig: ProjectModelConfig = { useCaseOverrides: overrides };

      await this.projectMetadataService.updateProjectMetadata(this.projectId, {
        modelConfig,
      });

      this.originalOverrides = new Map<string, string>();
      for (const row of this.useCaseRows) {
        if (row.isOverridden) {
          this.originalOverrides.set(row.useCase, row.selectedProfileId);
        }
      }
      this.hasUnsavedChanges = false;
      ClientLoggingService.log('INFO', '[ModelConfigCard] Saved model config overrides');
    } catch (err) {
      ClientLoggingService.log('ERROR', '[ModelConfigCard] Failed to save:', err);
      this.error = 'Failed to save model configuration.';
    } finally {
      this.saving = false;
    }
  }

  discardChanges(): void {
    for (const row of this.useCaseRows) {
      const originalOverride = this.originalOverrides.get(row.useCase);
      if (originalOverride) {
        row.selectedProfileId = originalOverride;
        row.isOverridden = true;
      } else {
        row.selectedProfileId = row.defaultProfileId;
        row.isOverridden = false;
      }
    }
    this.hasUnsavedChanges = false;
  }

  getProfileDisplayName(profileId: string): string {
    const profile = this.profiles.find(p => p.profileId === profileId);
    return profile?.displayName || profileId;
  }

  getSelectedProfile(row: UseCaseRow): ModelProfileSummary | undefined {
    return this.profiles.find(p => p.profileId === row.selectedProfileId);
  }
}
